import { useState } from 'react';
import { createPresentation, updatePresentation } from '../../../services/mentiService';

const SLIDE_TYPES = [
  { value: 'wordcloud', label: 'Ordsky', icon: 'cloud' },
  { value: 'multiplechoice', label: 'Fleirval', icon: 'check_circle' },
  { value: 'openended', label: 'Opne svar', icon: 'chat' },
  { value: 'scale', label: 'Skala', icon: 'linear_scale' },
  { value: 'content', label: 'Innhald', icon: 'article' },
];

const TYPE_LABELS = {
  wordcloud: 'Ordsky',
  multiplechoice: 'Fleirval',
  openended: 'Opne svar',
  scale: 'Skala',
  content: 'Innhald',
};

const TYPE_COLORS = {
  wordcloud: 'bg-purple-100 text-purple-700',
  multiplechoice: 'bg-blue-100 text-blue-700',
  openended: 'bg-green-100 text-green-700',
  scale: 'bg-orange-100 text-orange-700',
  content: 'bg-gray-100 text-gray-600',
};

function createEmptySlide(type = 'content') {
  const base = {
    id: `sl_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
    type,
    title: '',
    description: '',
    config: {},
  };

  switch (type) {
    case 'wordcloud':
      base.config = { maxWords: 2 };
      break;
    case 'multiplechoice':
      base.config = { options: ['', ''] };
      break;
    case 'openended':
      base.config = { maxLength: 200 };
      break;
    case 'scale':
      base.config = { minLabel: 'Lite', maxLabel: 'Mykje' };
      break;
    case 'content':
    default:
      base.config = {};
      break;
  }

  return base;
}

export default function MentiCreator({ presentation, onSave, onCancel }) {
  const isEditing = !!presentation;
  const [title, setTitle] = useState(presentation?.title || '');
  const [description, setDescription] = useState(presentation?.description || '');
  const [slides, setSlides] = useState(() => {
    if (presentation?.slides?.length > 0) {
      return presentation.slides.map(s => ({
        id: s.id || `sl_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
        type: s.type || 'content',
        title: s.title || '',
        description: s.description || '',
        config: s.config || {},
      }));
    }
    return [];
  });
  const [expandedSlide, setExpandedSlide] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);

  const addSlide = (type) => {
    const newSlide = createEmptySlide(type);
    setSlides([...slides, newSlide]);
    setExpandedSlide(newSlide.id);
    setShowTypeDropdown(false);
  };

  const removeSlide = (index) => {
    const updated = slides.filter((_, i) => i !== index);
    setSlides(updated);
    setExpandedSlide(null);
  };

  const moveSlide = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= slides.length) return;
    const updated = [...slides];
    const temp = updated[index];
    updated[index] = updated[newIndex];
    updated[newIndex] = temp;
    setSlides(updated);
  };

  const updateSlide = (index, field, value) => {
    const updated = [...slides];
    updated[index] = { ...updated[index], [field]: value };
    setSlides(updated);
  };

  const updateSlideConfig = (index, configField, value) => {
    const updated = [...slides];
    updated[index] = {
      ...updated[index],
      config: { ...updated[index].config, [configField]: value },
    };
    setSlides(updated);
  };

  const handleSave = async () => {
    if (!title.trim()) {
      alert('Presentasjonen treng ein tittel.');
      return;
    }
    if (slides.length === 0) {
      alert('Legg til minst eitt lysbilde.');
      return;
    }

    setSaving(true);
    try {
      const data = { title: title.trim(), description: description.trim(), slides };

      if (isEditing) {
        await updatePresentation(presentation.id, data);
      } else {
        await createPresentation(data);
      }
      onSave();
    } catch (error) {
      console.error('Error saving presentation:', error);
      alert('Klarte ikkje lagre presentasjonen. Prov igjen.');
    }
    setSaving(false);
  };

  const renderSlideConfig = (slide, index) => {
    switch (slide.type) {
      case 'wordcloud':
        return (
          <div className="mt-3">
            <label className="block text-sm font-medium text-gray-600 mb-1">Maks ord per svar</label>
            <div className="flex gap-2">
              {[1, 2, 3].map(n => (
                <button
                  key={n}
                  onClick={() => updateSlideConfig(index, 'maxWords', n)}
                  className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors ${
                    (slide.config.maxWords || 2) === n
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {n} ord
                </button>
              ))}
            </div>
          </div>
        );

      case 'multiplechoice':
        return (
          <div className="mt-3">
            <label className="block text-sm font-medium text-gray-600 mb-2">Svaralternativ</label>
            <div className="flex flex-col gap-2">
              {(slide.config.options || []).map((opt, optIdx) => (
                <div key={optIdx} className="flex items-center gap-2">
                  <span className="text-sm font-bold text-blue-600 w-6">{String.fromCharCode(65 + optIdx)}</span>
                  <input
                    type="text"
                    value={opt}
                    onChange={(e) => {
                      const newOpts = [...(slide.config.options || [])];
                      newOpts[optIdx] = e.target.value;
                      updateSlideConfig(index, 'options', newOpts);
                    }}
                    className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                    placeholder={`Alternativ ${String.fromCharCode(65 + optIdx)}`}
                  />
                  {(slide.config.options || []).length > 2 && (
                    <button
                      onClick={() => {
                        const newOpts = (slide.config.options || []).filter((_, i) => i !== optIdx);
                        updateSlideConfig(index, 'options', newOpts);
                      }}
                      className="text-gray-400 hover:text-red-500 transition-colors"
                    >
                      <span className="material-symbols-outlined text-[18px]">close</span>
                    </button>
                  )}
                </div>
              ))}
              {(slide.config.options || []).length < 6 && (
                <button
                  onClick={() => {
                    const newOpts = [...(slide.config.options || []), ''];
                    updateSlideConfig(index, 'options', newOpts);
                  }}
                  className="flex items-center gap-1 text-blue-600 text-sm font-medium hover:text-blue-500 transition-colors mt-1"
                >
                  <span className="material-symbols-outlined text-[16px]">add</span>
                  Legg til alternativ
                </button>
              )}
            </div>
          </div>
        );

      case 'scale':
        return (
          <div className="mt-3 grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Minimum-etikett</label>
              <input
                type="text"
                value={slide.config.minLabel || ''}
                onChange={(e) => updateSlideConfig(index, 'minLabel', e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                placeholder="T.d. Lite"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Maksimum-etikett</label>
              <input
                type="text"
                value={slide.config.maxLabel || ''}
                onChange={(e) => updateSlideConfig(index, 'maxLabel', e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                placeholder="T.d. Mykje"
              />
            </div>
          </div>
        );

      case 'openended':
        return (
          <div className="mt-3">
            <label className="block text-sm font-medium text-gray-600 mb-1">Maks lengd (teikn)</label>
            <input
              type="number"
              value={slide.config.maxLength || 200}
              onChange={(e) => updateSlideConfig(index, 'maxLength', parseInt(e.target.value, 10) || 200)}
              className="w-32 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
              min={10}
              max={1000}
            />
          </div>
        );

      case 'content':
      default:
        return (
          <div className="mt-3">
            <p className="text-sm text-gray-400 italic">Innhaldslysbilde har ingen ekstra innstillingar.</p>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-background-light font-display text-[#0d121b] pb-8">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-background-light/95 backdrop-blur-sm p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={onCancel}
            className="flex size-10 items-center justify-center rounded-full bg-white shadow-ambient hover:bg-gray-50 transition-colors"
          >
            <span className="material-symbols-outlined text-gray-600">arrow_back</span>
          </button>
          <h2 className="text-xl font-bold">{isEditing ? 'Rediger presentasjon' : 'Ny presentasjon'}</h2>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl font-bold text-sm hover:brightness-110 transition-all disabled:opacity-50"
        >
          {saving ? 'Lagrar...' : 'Lagre'}
        </button>
      </header>

      <div className="px-4 space-y-4">
        {/* Title & Description */}
        <div className="bg-white rounded-2xl shadow-ambient p-4 space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">Tittel</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl text-base font-medium focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-blue-400"
              placeholder="Namn pa presentasjonen"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">Skildring (valfritt)</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl text-base focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-blue-400"
              placeholder="Kort skildring"
            />
          </div>
        </div>

        {/* Slides */}
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold">Lysbilete ({slides.length})</h3>
          <div className="relative">
            <button
              onClick={() => setShowTypeDropdown(!showTypeDropdown)}
              className="flex items-center gap-1 px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-bold hover:bg-blue-700 transition-colors"
            >
              <span className="material-symbols-outlined text-[18px]">add</span>
              Legg til lysbilde
            </button>
            {showTypeDropdown && (
              <div className="absolute right-0 mt-2 w-52 bg-white rounded-xl shadow-lg border border-gray-100 py-2 z-50">
                {SLIDE_TYPES.map(st => (
                  <button
                    key={st.value}
                    onClick={() => addSlide(st.value)}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm font-medium hover:bg-blue-50 transition-colors"
                  >
                    <span className="material-symbols-outlined text-[18px] text-blue-600">{st.icon}</span>
                    {st.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {slides.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center bg-white rounded-2xl shadow-ambient">
            <span className="material-symbols-outlined text-5xl text-gray-300 mb-3">layers</span>
            <p className="text-gray-500 font-medium">Ingen lysbilete enno</p>
            <p className="text-gray-400 text-sm mt-1">Bruk knappen over for a leggje til lysbilete</p>
          </div>
        ) : (
          <div className="space-y-3">
            {slides.map((slide, index) => {
              const isExpanded = expandedSlide === slide.id;
              const typeLabel = TYPE_LABELS[slide.type] || slide.type;
              const typeColor = TYPE_COLORS[slide.type] || 'bg-gray-100 text-gray-600';

              return (
                <div key={slide.id} className="bg-white rounded-2xl shadow-ambient overflow-hidden">
                  {/* Collapsed header */}
                  <button
                    onClick={() => setExpandedSlide(isExpanded ? null : slide.id)}
                    className="w-full flex items-center gap-3 p-4 text-left hover:bg-gray-50 transition-colors"
                  >
                    <span className="text-sm font-bold text-gray-400 w-6">{index + 1}</span>
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${typeColor}`}>{typeLabel}</span>
                    <span className="flex-1 text-sm font-medium truncate">{slide.title || 'Utan tittel'}</span>
                    <span className="material-symbols-outlined text-gray-400 text-[18px]">
                      {isExpanded ? 'expand_less' : 'expand_more'}
                    </span>
                  </button>

                  {/* Expanded body */}
                  {isExpanded && (
                    <div className="px-4 pb-4 border-t border-gray-100 pt-3">
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-600 mb-1">Tittel</label>
                          <input
                            type="text"
                            value={slide.title}
                            onChange={(e) => updateSlide(index, 'title', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                            placeholder="Lysbilete-tittel"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-600 mb-1">Skildring</label>
                          <textarea
                            value={slide.description}
                            onChange={(e) => updateSlide(index, 'description', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 resize-none"
                            rows={2}
                            placeholder="Valfri skildring"
                          />
                        </div>

                        {/* Type-specific config */}
                        {renderSlideConfig(slide, index)}

                        {/* Actions */}
                        <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
                          <button
                            onClick={() => moveSlide(index, -1)}
                            disabled={index === 0}
                            className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-30 transition-colors"
                            title="Flytt opp"
                          >
                            <span className="material-symbols-outlined text-[18px]">arrow_upward</span>
                          </button>
                          <button
                            onClick={() => moveSlide(index, 1)}
                            disabled={index === slides.length - 1}
                            className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-30 transition-colors"
                            title="Flytt ned"
                          >
                            <span className="material-symbols-outlined text-[18px]">arrow_downward</span>
                          </button>
                          <div className="flex-1" />
                          <button
                            onClick={() => removeSlide(index)}
                            className="flex items-center gap-1 px-3 py-1.5 text-red-500 hover:bg-red-50 rounded-lg text-sm font-medium transition-colors"
                          >
                            <span className="material-symbols-outlined text-[16px]">delete</span>
                            Slett
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Bottom actions */}
        <div className="flex gap-3 pt-4">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl font-bold text-sm hover:bg-gray-200 transition-colors"
          >
            Avbryt
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl font-bold text-sm hover:brightness-110 transition-all disabled:opacity-50"
          >
            {saving ? 'Lagrar...' : (isEditing ? 'Oppdater' : 'Lagre')}
          </button>
        </div>
      </div>
    </div>
  );
}
